import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  RiAddLine,
  RiEditLine,
  RiDeleteBinLine,
  RiEyeLine,
  RiSearchLine,
  RiCloseLine,
  RiCheckLine,
  RiPriceTag3Line,
  RiTrophyLine,
  RiTimeLine,
  RiUserStarLine,
  RiSparklingLine,
  RiInformationLine,
  RiShieldCheckLine,
  RiFootballLine,
  RiBasketballLine,
  RiPercentLine,
  RiCake2Line,
} from "react-icons/ri";
import { Sparkles, Package, Users, ShieldAlert, Award, ArrowUpRight, Pizza } from "lucide-react";
import Swal from "sweetalert2";
import toast from "react-hot-toast";
import ModalWrapper from "../../components/common/ModalWrapper";
import ToggleSwitch from "../../components/AdminSideComponents/JobPost/ToggleSwitch";
import {
  FaPizzaSlice,
  FaUtensils,
  FaGlassCheers,
  FaLemon,
  FaBirthdayCake,
} from "react-icons/fa";

const iconMapping = {
  FaPizzaSlice: <FaPizzaSlice />,
  FaUtensils: <FaUtensils />,
  FaGlassCheers: <FaGlassCheers />,
  FaLemon: <FaLemon />,
  FaBirthdayCake: <FaBirthdayCake />,
};

const iconOptions = [
  "FaPizzaSlice",
  "FaUtensils",
  "FaGlassCheers",
  "FaLemon",
  "FaBirthdayCake",
];

const AdminEventPackages = () => {
  const API = process.env.REACT_APP_API_URL;

  const [activeTab, setActiveTab] = useState("packages"); // "packages" | "addons" | "policies"
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [packages, setPackages] = useState([]);


  const [addons, setAddons] = useState([
    {
      _id: "add_1",
      title: "ADDITIONAL PARTICIPANTS",
      desc: "$20, $25, or $30 per extra player beyond package limit based on tier.",
      category: "Player Capacity",
      isActive: true,
    },
    {
      _id: "add_2",
      title: "ADDITIONAL SPORT",
      desc: "Split your 90 minutes across two sports (e.g. 45 min Soccer + 45 min Basketball).",
      category: "Sports Customization",
      isActive: true,
    },
    {
      _id: "add_3",
      title: "FOOD & BEVERAGES",
      desc: "Piping hot artisan pizza, finger food shareables, and unlimited soda hydration stations.",
      category: "Catering",
      isActive: true,
    },
    {
      _id: "add_4",
      title: "LARGER GROUPS (25+ TO 100+)",
      desc: "Custom arena buyouts, dual court setups, and corporate tournament brackets.",
      category: "Large Groups",
      isActive: true,
    },
    {
      _id: "add_5",
      title: "SPECIAL REQUESTS",
      desc: "Custom music playlists, tournament refereeing, trophy presentations, or custom party times.",
      category: "Experience",
      isActive: true,
    },
  ]);

  const [policies, setPolicies] = useState([
    {
      _id: "pol_1",
      title: "Participant Count",
      desc: "Package limits apply to participating guests. Non-participating parents, guardians, and spectators do not count toward the participant limit, subject to facility capacity.",
      category: "General",
      isActive: true,
    },
    {
      _id: "pol_2",
      title: "Sport Selection",
      desc: "All-Star Squad and Big League include one sport. Additional sports may be available for an additional charge, subject to scheduling and facility availability.",
      category: "Sports",
      isActive: true,
    },
    {
      _id: "pol_3",
      title: "Playing Area",
      desc: "Strikes & Shots will assign the appropriate field or court based on the selected sport, group size, scheduling, and facility availability.",
      category: "Facility",
      isActive: true,
    },
    {
      _id: "pol_4",
      title: "Food & Beverages",
      desc: "Outside food and beverages are not permitted, with the exception of birthday cakes or cupcakes. Food and beverage packages may be added to any event.",
      category: "Catering",
      isActive: true,
    },
    {
      _id: "pol_5",
      title: "Party Supplies",
      desc: "Paper plates, cups, napkins, and utensils are not included in base packages unless food and beverage packages are purchased through Strikes & Shots.",
      category: "Supplies",
      isActive: true,
    },
    {
      _id: "pol_6",
      title: "Facility Availability",
      desc: "All packages, field/court space, and party areas are subject to availability at the time of booking. Early reservation is recommended.",
      category: "Booking",
      isActive: true,
    },
    {
      _id: "pol_7",
      title: "Waiver Requirement",
      desc: "All participants must have a signed electronic waiver completed prior to participating in any sports or activities at Strikes & Shots.",
      category: "Legal & Safety",
      isActive: true,
    },
    {
      _id: "pol_8",
      title: "Large Groups",
      desc: "For groups of 25 or more participants, contact Strikes & Shots for customized pricing, space, and staffing requirements.",
      category: "Group Events",
      isActive: true,
    },
  ]);

  // Modal States
  const [isPackageModalOpen, setIsPackageModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isAddonModalOpen, setIsAddonModalOpen] = useState(false);
  const [isPolicyModalOpen, setIsPolicyModalOpen] = useState(false);
  const [isFoodModalOpen, setIsFoodModalOpen] = useState(false);

  const [foodCards, setFoodCards] = useState([]);
  const [isFoodSectionVisible, setIsFoodSectionVisible] = useState(true);

  const [selectedItem, setSelectedItem] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  // Form Data State for Package
  const [packageFormData, setPackageFormData] = useState({
    name: "",
    price: "",
    currency: "$",
    partySize: "Up to 10 players",
    baseParticipants: 10,
    maxParticipants: 12,
    extraParticipantPrice: 20,
    sportsActivityTime: "90 minutes",
    partySocialAreaTime: "45-60 minutes",
    sportChoice: "Choose 1 Sport",
    playingArea: "Dedicated field/court appropriate for selected sport",
    badge: "",
    isPopular: false,
    isActive: true,
    features: [""],
  });

  // Form Data State for Add-on
  const [addonFormData, setAddonFormData] = useState({
    title: "",
    desc: "",
    category: "Player Capacity",
    isActive: true,
  });

  // Form Data State for Policy
  const [policyFormData, setPolicyFormData] = useState({
    title: "",
    desc: "",
    category: "General",
    isActive: true,
  });

  // Form Data State for Food
  const [foodFormData, setFoodFormData] = useState({
    idStr: "",
    title: "",
    tagline: "",
    iconName: "FaUtensils",
    desc: "",
    options: [{ name: "", desc: "" }],
    isActive: true,
    order: 0,
  });

  // =================== Fetch Data from Backend API ===================
  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/event-packages/admin/all`);
      if (res.data?.success) {
        if (res.data.packages?.length > 0) {
          setPackages(res.data.packages);
        }
        if (res.data.addons?.length > 0) {
          setAddons(res.data.addons);
        }
        if (res.data.policies?.length > 0) {
          setPolicies(res.data.policies);
        }
      }

      const foodRes = await axios.get(`${API}/food-and-beverages?all=true`);
      if (foodRes.data?.success) {
        setFoodCards(foodRes.data.data || []);
        if (foodRes.data.settings) {
          setIsFoodSectionVisible(foodRes.data.settings.isSectionVisible);
        }
      }
    } catch (error) {
      console.error("Error loading event packages:", error);
      toast.error("Failed to load packages from server");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // =================== Handlers for Packages ===================
  const handleOpenCreatePackage = () => {
    setIsEditing(false);
    setPackageFormData({
      name: "",
      price: "",
      currency: "$",
      partySize: "Up to 10 players",
      baseParticipants: 10,
      maxParticipants: 12,
      extraParticipantPrice: 20,
      sportsActivityTime: "90 minutes",
      partySocialAreaTime: "45-60 minutes",
      sportChoice: "Choose 1 Sport",
      playingArea: "Dedicated field/court appropriate for selected sport",
      badge: "",
      isPopular: false,
      isActive: true,
      features: [
        "90 Minutes of Sports with arena equipment",
        "45–60 Minutes in dedicated Private Party Bay",
        "Tables & Chairs arranged for all guests",
        "Complete Setup & Cleanup included",
      ],
    });
    setIsPackageModalOpen(true);
  };

  const handleOpenEditPackage = (pkg) => {
    setIsEditing(true);
    setSelectedItem(pkg);
    setPackageFormData({
      ...pkg,
      features: pkg.features?.length > 0 ? [...pkg.features] : [""],
    });
    setIsPackageModalOpen(true);
  };

  const handleOpenViewPackage = (pkg) => {
    setSelectedItem(pkg);
    setIsViewModalOpen(true);
  };

  const handleTogglePackageStatus = async (id) => {
    try {
      const res = await axios.patch(`${API}/event-packages/toggle-status/${id}`);
      if (res.data?.success) {
        setPackages((prev) =>
          prev.map((item) =>
            item._id === id ? { ...item, isActive: res.data.package.isActive } : item
          )
        );
        toast.success(res.data.message || "Package status updated successfully!");
      }
    } catch (error) {
      console.error("Error toggling status:", error);
      toast.error("Failed to toggle package status.");
    }
  };

  const handleDeletePackage = (id, name) => {
    Swal.fire({
      title: "Are you sure?",
      text: `Delete package "${name}"? This action cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#CD1B1B",
      cancelButtonColor: "#050733",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const res = await axios.delete(`${API}/event-packages/delete/${id}`);
          if (res.data?.success) {
            setPackages((prev) => prev.filter((p) => p._id !== id));
            toast.success(`Package "${name}" deleted successfully.`);
          }
        } catch (error) {
          console.error("Error deleting package:", error);
          toast.error("Failed to delete package.");
        }
      }
    });
  };

  const handleSavePackage = async (e) => {
    e.preventDefault();
    if (!packageFormData.name.trim() || !packageFormData.price) {
      toast.error("Please provide a package name and valid price.");
      return;
    }

    const payload = {
      ...packageFormData,
      price: Number(packageFormData.price),
      baseParticipants: Number(packageFormData.baseParticipants) || 10,
      maxParticipants: Number(packageFormData.maxParticipants) || 12,
      extraParticipantPrice: Number(packageFormData.extraParticipantPrice) || 20,
      features: packageFormData.features.filter((f) => f && f.trim() !== ""),
    };

    setSubmitting(true);
    try {
      if (isEditing && selectedItem) {
        const res = await axios.put(
          `${API}/event-packages/update/${selectedItem._id}`,
          payload
        );
        if (res.data?.success) {
          setPackages((prev) =>
            prev.map((p) => (p._id === selectedItem._id ? res.data.package : p))
          );
          toast.success("Package updated successfully!");
          setIsPackageModalOpen(false);
        }
      } else {
        const res = await axios.post(`${API}/event-packages/create`, payload);
        if (res.data?.success) {
          setPackages((prev) => [res.data.package, ...prev]);
          toast.success("New package created successfully!");
          setIsPackageModalOpen(false);
        }
      }
    } catch (error) {
      console.error("Error saving package:", error);
      toast.error(error.response?.data?.message || "Failed to save package.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleResetDefaults = () => {
    Swal.fire({
      title: "Reset to Standard Defaults?",
      text: "This will restore the 3 standard Strikes & Shots squad packages.",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#050733",
      cancelButtonColor: "#CD1B1B",
      confirmButtonText: "Yes, reset packages",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const res = await axios.post(`${API}/event-packages/seed`);
          if (res.data?.success) {
            setPackages(res.data.packages);
            toast.success("Standard packages restored!");
          }
        } catch (error) {
          console.error("Error resetting defaults:", error);
          toast.error("Failed to reset packages.");
        }
      }
    });
  };

  const handleFeatureChange = (index, value) => {
    const updated = [...packageFormData.features];
    updated[index] = value;
    setPackageFormData({ ...packageFormData, features: updated });
  };

  const handleAddFeature = () => {
    setPackageFormData({
      ...packageFormData,
      features: [...packageFormData.features, ""],
    });
  };

  const handleRemoveFeature = (index) => {
    if (packageFormData.features.length <= 1) return;
    const updated = packageFormData.features.filter((_, i) => i !== index);
    setPackageFormData({ ...packageFormData, features: updated });
  };

  // =================== Handlers for Add-ons ===================
  const handleOpenCreateAddon = () => {
    setIsEditing(false);
    setAddonFormData({
      title: "",
      desc: "",
      category: "Player Capacity",
      isActive: true,
    });
    setIsAddonModalOpen(true);
  };

  const handleOpenEditAddon = (addon) => {
    setIsEditing(true);
    setSelectedItem(addon);
    setAddonFormData({ ...addon });
    setIsAddonModalOpen(true);
  };

  const handleDeleteAddon = (id, title) => {
    Swal.fire({
      title: "Delete Add-on?",
      text: `Remove "${title}" from customizations?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#CD1B1B",
      cancelButtonColor: "#050733",
      confirmButtonText: "Yes, delete",
    }).then((result) => {
      if (result.isConfirmed) {
        setAddons((prev) => prev.filter((a) => a._id !== id));
        toast.success("Add-on deleted successfully.");
      }
    });
  };

  const handleSaveAddon = (e) => {
    e.preventDefault();
    if (!addonFormData.title.trim()) {
      toast.error("Please provide an add-on title.");
      return;
    }

    if (isEditing && selectedItem) {
      setAddons((prev) =>
        prev.map((a) =>
          a._id === selectedItem._id ? { ...a, ...addonFormData } : a
        )
      );
      toast.success("Add-on updated successfully!");
    } else {
      setAddons((prev) => [
        ...prev,
        { _id: `add_${Date.now()}`, ...addonFormData },
      ]);
      toast.success("Add-on created successfully!");
    }
    setIsAddonModalOpen(false);
  };

  // =================== Handlers for Policies ===================
  const handleOpenCreatePolicy = () => {
    setIsEditing(false);
    setPolicyFormData({
      title: "",
      desc: "",
      category: "General",
      isActive: true,
    });
    setIsPolicyModalOpen(true);
  };

  const handleOpenEditPolicy = (policy) => {
    setIsEditing(true);
    setSelectedItem(policy);
    setPolicyFormData({ ...policy });
    setIsPolicyModalOpen(true);
  };

  const handleDeletePolicy = (id, title) => {
    Swal.fire({
      title: "Delete Policy?",
      text: `Remove policy "${title}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#CD1B1B",
      cancelButtonColor: "#050733",
      confirmButtonText: "Yes, delete",
    }).then((result) => {
      if (result.isConfirmed) {
        setPolicies((prev) => prev.filter((p) => p._id !== id));
        toast.success("Policy deleted successfully.");
      }
    });
  };

  const handleSavePolicy = (e) => {
    e.preventDefault();
    if (!policyFormData.title.trim() || !policyFormData.desc.trim()) {
      toast.error("Title and description are required.");
      return;
    }

    if (isEditing && selectedItem) {
      setPolicies((prev) =>
        prev.map((p) =>
          p._id === selectedItem._id ? { ...p, ...policyFormData } : p
        )
      );
      toast.success("Policy updated successfully!");
    } else {
      setPolicies((prev) => [
        ...prev,
        { _id: `pol_${Date.now()}`, ...policyFormData },
      ]);
      toast.success("Policy created successfully!");
    }
    setIsPolicyModalOpen(false);
  };

  // =================== Handlers for Food & Beverage ===================
  const handleOpenCreateFood = () => {
    setIsEditing(false);
    setSelectedItem(null);
    setFoodFormData({
      idStr: `food_${Date.now()}`,
      title: "",
      tagline: "",
      iconName: "FaUtensils",
      desc: "",
      options: [{ name: "", desc: "" }],
      isActive: true,
      order: foodCards.length + 1,
    });
    setIsFoodModalOpen(true);
  };

  const handleOpenEditFood = (item) => {
    setIsEditing(true);
    setSelectedItem(item);
    setFoodFormData({
      ...item,
      options: item.options?.length > 0 ? [...item.options] : [{ name: "", desc: "" }],
    });
    setIsFoodModalOpen(true);
  };

  const handleDeleteFood = (id, title) => {
    Swal.fire({
      title: "Delete Item?",
      text: `Remove "${title}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#CD1B1B",
      cancelButtonColor: "#050733",
      confirmButtonText: "Yes, delete",
    }).then((result) => {
      if (result.isConfirmed) {
        setFoodCards((prev) => prev.filter((a) => a._id !== id));
        toast.success("Item deleted successfully.");
      }
    });
  };

  const handleSaveFood = async (e) => {
    e.preventDefault();
    if (!foodFormData.title.trim()) {
      toast.error("Please provide a title.");
      return;
    }

    setSubmitting(true);
    try {
      if (isEditing && selectedItem) {
        setFoodCards((prev) =>
          prev.map((item) => (item._id === selectedItem._id ? { ...item, ...foodFormData } : item))
        );
        toast.success("Updated successfully! (Local state only)");
      } else {
        const res = await axios.post(`${API}/food-and-beverages`, foodFormData);
        if (res.data?.success) {
          setFoodCards((prev) => [...prev, res.data.data]);
          toast.success("Created successfully!");
        }
      }
      setIsFoodModalOpen(false);
    } catch (error) {
      console.error("Error saving:", error);
      toast.error("Failed to save.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleFoodStatus = async (id) => {
    try {
      const res = await axios.patch(`${API}/food-and-beverages/toggle-status/${id}`);
      if (res.data?.success) {
        setFoodCards((prev) =>
          prev.map((item) =>
            item._id === id ? { ...item, isActive: res.data.data.isActive } : item
          )
        );
        toast.success("Status toggled successfully!");
      }
    } catch (error) {
      console.error("Error toggling food status:", error);
      toast.error("Failed to toggle status.");
    }
  };

  const handleToggleFoodSectionVisibility = async () => {
    try {
      const res = await axios.patch(`${API}/food-and-beverages/toggle-section`);
      if (res.data?.success) {
        setIsFoodSectionVisible(res.data.settings.isSectionVisible);
        toast.success(
          res.data.settings.isSectionVisible
            ? "Food & Beverage section is now visible to users"
            : "Food & Beverage section is now hidden from users"
        );
      }
    } catch (error) {
      console.error("Error toggling section visibility:", error);
      toast.error("Failed to toggle section visibility.");
    }
  };

  const handleOptionChange = (index, field, value) => {
    const updated = [...foodFormData.options];
    updated[index][field] = value;
    setFoodFormData({ ...foodFormData, options: updated });
  };

  const handleAddOption = () => {
    setFoodFormData({
      ...foodFormData,
      options: [...foodFormData.options, { name: "", desc: "" }],
    });
  };

  const handleRemoveOption = (index) => {
    if (foodFormData.options.length <= 1) return;
    const updated = foodFormData.options.filter((_, i) => i !== index);
    setFoodFormData({ ...foodFormData, options: updated });
  };

  // Filtering helper
  const filteredPackages = packages.filter((pkg) => {
    const matchesSearch =
      pkg.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      pkg.price.toString().includes(searchQuery);
    const matchesStatus =
      statusFilter === "all" ||
      (statusFilter === "active" && pkg.isActive) ||
      (statusFilter === "inactive" && !pkg.isActive);
    return matchesSearch && matchesStatus;
  });

  const filteredAddons = addons.filter((addon) =>
    addon.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    addon.desc.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredPolicies = policies.filter((pol) =>
    pol.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    pol.desc.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredFoodCards = foodCards.filter(
    (c) =>
      c.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.desc.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-transparent p-4 md:p-6 w-full">
      {/* Top Header */}
      <div className="flex flex-col gap-4 mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-heading font-bold bg-gradient-to-r from-primaryBtnColor to-primaryBtnHoverColor bg-clip-text text-transparent inline-block">
              Event Package Management
            </h1>
            <p className="text-secondaryTextColor text-sm mt-1 font-subheading">
              Manage squad packages, player capacities, pricing tiers, custom add-ons, and facility policies.
            </p>
          </div>

          {/* Primary Action Button */}
          <div className="flex items-center gap-3">
            {activeTab === "packages" && (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleResetDefaults}
                  className="bg-white border border-gray-200 text-gray-700 hover:text-primaryBtnHoverColor hover:border-gray-300 py-2.5 px-3.5 rounded-xl flex items-center gap-1.5 shadow-sm text-xs font-semibold cursor-pointer transition-all"
                  title="Reset packages to standard defaults"
                >
                  <RiSparklingLine size={16} />
                  <span className="hidden sm:inline">Reset Defaults</span>
                </button>
                <button
                  onClick={handleOpenCreatePackage}
                  className="bg-gradient-to-r from-primaryBtnColor to-primaryBtnHoverColor text-white py-2.5 px-4 rounded-xl flex items-center gap-2 shadow-lg hover:shadow-xl transition-all duration-200 cursor-pointer text-sm font-semibold hover:scale-[1.02] active:scale-95"
                >
                  <RiAddLine size={18} />
                  <span>Create Package</span>
                </button>
              </div>
            )}
            {activeTab === "addons" && (
              <button
                onClick={handleOpenCreateAddon}
                className="bg-gradient-to-r from-primaryBtnColor to-primaryBtnHoverColor text-white py-2.5 px-4 rounded-xl flex items-center gap-2 shadow-lg hover:shadow-xl transition-all duration-200 cursor-pointer text-sm font-semibold hover:scale-[1.02] active:scale-95"
              >
                <RiAddLine size={18} />
                <span>Add Add-on</span>
              </button>
            )}
            {activeTab === "policies" && (
              <button
                onClick={handleOpenCreatePolicy}
                className="bg-gradient-to-r from-primaryBtnColor to-primaryBtnHoverColor text-white py-2.5 px-4 rounded-xl flex items-center gap-2 shadow-lg hover:shadow-xl transition-all duration-200 cursor-pointer text-sm font-semibold hover:scale-[1.02] active:scale-95"
              >
                <RiAddLine size={18} />
                <span>Add Policy</span>
              </button>
            )}
            {activeTab === "food" && (
              <button
                onClick={handleOpenCreateFood}
                className="bg-gradient-to-r from-primaryBtnColor to-primaryBtnHoverColor text-white py-2.5 px-4 rounded-xl flex items-center gap-2 shadow-lg hover:shadow-xl transition-all duration-200 cursor-pointer text-sm font-semibold hover:scale-[1.02] active:scale-95"
              >
                <RiAddLine size={18} />
                <span>Add Menu Item</span>
              </button>
            )}
          </div>
        </div>

        {/* 4 Summary Stat Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-2">
          {/* Stat 1 */}
          <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm flex items-center justify-between">
            <div>
              <span className="text-xs font-bold text-gray-500 uppercase tracking-wider block">
                Total Packages
              </span>
              <div className="text-2xl font-black text-primaryTextColor font-heading mt-1">
                {packages.length} Tiers
              </div>
              <span className="text-[11px] text-emerald-600 font-semibold mt-1 flex items-center gap-1">
                <RiCheckLine /> {packages.filter((p) => p.isActive).length} Active online
              </span>
            </div>
            <div className="w-12 h-12 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center text-xl shadow-inner">
              <Package size={22} />
            </div>
          </div>

          {/* Stat 2 */}
          <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm flex items-center justify-between">
            <div>
              <span className="text-xs font-bold text-gray-500 uppercase tracking-wider block">
                Most Popular
              </span>
              <div className="text-lg font-black text-primaryTextColor font-heading mt-1 truncate max-w-[150px]">
                All-Star Squad
              </div>
              <span className="text-[11px] text-primaryBtnHoverColor font-semibold mt-1 flex items-center gap-1">
                <Sparkles size={12} /> Best Value Match
              </span>
            </div>
            <div className="w-12 h-12 rounded-xl bg-red-50 text-primaryBtnHoverColor flex items-center justify-center text-xl shadow-inner">
              <Award size={22} />
            </div>
          </div>

          {/* Stat 3 */}
          <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm flex items-center justify-between">
            <div>
              <span className="text-xs font-bold text-gray-500 uppercase tracking-wider block">
                Price Range
              </span>
              <div className="text-2xl font-black text-primaryTextColor font-heading mt-1">
                $275 – $475
              </div>
              <span className="text-[11px] text-gray-500 font-semibold mt-1 block">
                Base per participant: ~$23–$27
              </span>
            </div>
            <div className="w-12 h-12 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center text-xl shadow-inner">
              <RiPriceTag3Line size={22} />
            </div>
          </div>

          {/* Stat 4 */}
          <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm flex items-center justify-between">
            <div>
              <span className="text-xs font-bold text-gray-500 uppercase tracking-wider block">
                Add-ons &amp; Policies
              </span>
              <div className="text-2xl font-black text-primaryTextColor font-heading mt-1">
                {addons.length + policies.length} Configured
              </div>
              <span className="text-[11px] text-gray-500 font-semibold mt-1 block">
                {addons.length} Add-ons &bull; {policies.length} Policies
              </span>
            </div>
            <div className="w-12 h-12 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center text-xl shadow-inner">
              <ShieldAlert size={22} />
            </div>
          </div>
        </div>

        {/* Tab Buttons & Search Filter Row */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mt-4">
          {/* Tabs */}
          <div className="flex items-center gap-2 border-b border-gray-200 pb-2 md:pb-0 md:border-none">
            <button
              onClick={() => setActiveTab("packages")}
              className={`px-5 py-2.5 rounded-xl font-heading font-bold text-xs sm:text-sm tracking-wide uppercase transition-all duration-200 flex items-center gap-2 cursor-pointer ${activeTab === "packages"
                ? "bg-primaryBtnColor text-white shadow-md shadow-slate-900/20"
                : "bg-white text-gray-600 hover:text-gray-900 border border-gray-200 hover:bg-gray-50"
                }`}
            >
              <Package size={16} />
              <span>Packages &amp; Pricing</span>
              <span
                className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${activeTab === "packages"
                  ? "bg-white/20 text-white"
                  : "bg-gray-100 text-gray-600"
                  }`}
              >
                {packages.length}
              </span>
            </button>

            <button
              onClick={() => setActiveTab("addons")}
              className={`px-5 py-2.5 rounded-xl font-heading font-bold text-xs sm:text-sm tracking-wide uppercase transition-all duration-200 flex items-center gap-2 cursor-pointer ${activeTab === "addons"
                ? "bg-primaryBtnColor text-white shadow-md shadow-slate-900/20"
                : "bg-white text-gray-600 hover:text-gray-900 border border-gray-200 hover:bg-gray-50"
                }`}
            >
              <Sparkles size={16} />
              <span>Add-ons &amp; Upgrades</span>
              <span
                className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${activeTab === "addons"
                  ? "bg-white/20 text-white"
                  : "bg-gray-100 text-gray-600"
                  }`}
              >
                {addons.length}
              </span>
            </button>

            <button
              onClick={() => setActiveTab("policies")}
              className={`px-5 py-2.5 rounded-xl font-heading font-bold text-xs sm:text-sm tracking-wide uppercase transition-all duration-200 flex items-center gap-2 cursor-pointer ${activeTab === "policies"
                ? "bg-primaryBtnColor text-white shadow-md shadow-slate-900/20"
                : "bg-white text-gray-600 hover:text-gray-900 border border-gray-200 hover:bg-gray-50"
                }`}
            >
              <RiShieldCheckLine size={16} />
              <span>Notice &amp; Policies</span>
              <span
                className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${activeTab === "policies"
                  ? "bg-white/20 text-white"
                  : "bg-gray-100 text-gray-600"
                  }`}
              >
                {policies.length}
              </span>
            </button>

            <button
              onClick={() => setActiveTab("food")}
              className={`px-5 py-2.5 rounded-xl font-heading font-bold text-xs sm:text-sm tracking-wide uppercase transition-all duration-200 flex items-center gap-2 cursor-pointer ${activeTab === "food"
                ? "bg-primaryBtnColor text-white shadow-md shadow-slate-900/20"
                : "bg-white text-gray-600 hover:text-gray-900 border border-gray-200 hover:bg-gray-50"
                }`}
            >
              <RiCake2Line size={16} />
              <span>Food &amp; Beverages</span>
            </button>
          </div>

          {/* Search & Status Filter */}
          <div className="flex items-center gap-3">
            <div className="relative flex-1 sm:w-64">
              <RiSearchLine className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search items..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-white border border-gray-200 rounded-xl text-xs sm:text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-primaryBtnHoverColor focus:ring-1 focus:ring-primaryBtnHoverColor transition-all"
              />
            </div>

            {activeTab === "packages" && (
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 bg-white border border-gray-200 rounded-xl text-xs sm:text-sm text-gray-800 focus:outline-none focus:border-primaryBtnHoverColor cursor-pointer"
              >
                <option value="all">All Status</option>
                <option value="active">Active Only</option>
                <option value="inactive">Inactive</option>
              </select>
            )}
          </div>
        </div>
      </div>

      {/* ===================== TAB 1: PACKAGES & PRICING ===================== */}
      {activeTab === "packages" && (
        <>
          {loading ? (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-12">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 animate-pulse flex flex-col justify-between"
                >
                  <div className="h-1.5 bg-gray-200"></div>
                  <div className="p-6 space-y-4">
                    <div className="h-6 bg-gray-200 rounded-lg w-2/3"></div>
                    <div className="h-4 bg-gray-100 rounded w-1/2"></div>
                    <div className="h-16 bg-gray-100 rounded-xl my-4"></div>
                    <div className="space-y-2">
                      <div className="h-3 bg-gray-100 rounded w-full"></div>
                      <div className="h-3 bg-gray-100 rounded w-5/6"></div>
                      <div className="h-3 bg-gray-100 rounded w-4/6"></div>
                    </div>
                  </div>
                  <div className="p-4 bg-gray-50 border-t border-gray-100 h-12"></div>
                </div>
              ))}
            </div>
          ) : filteredPackages.length === 0 ? (
            <div className="bg-white rounded-2xl p-12 text-center border border-gray-100 shadow-sm mb-12">
              <Package size={48} className="mx-auto text-gray-300 mb-3" />
              <h3 className="text-lg font-heading font-bold text-gray-700">No Packages Found</h3>
              <p className="text-xs text-gray-500 font-subheading mt-1 max-w-sm mx-auto">
                No event packages match your current search query or filter. Try clearing filters or create a new package.
              </p>
              <button
                type="button"
                onClick={handleResetDefaults}
                className="mt-4 px-4 py-2 bg-primaryBtnColor text-white text-xs font-semibold rounded-xl hover:bg-primaryBtnHoverColor transition-all cursor-pointer inline-flex items-center gap-2"
              >
                <RiSparklingLine size={14} /> Restore Standard Packages
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-12">
              {filteredPackages.map((pkg) => (
                <div
                  key={pkg._id}
                  className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 relative border border-gray-100 flex flex-col justify-between"
                >
                  {/* Top Accent Gradient Bar */}
                  <div className="h-1.5 bg-gradient-to-r from-primaryBtnColor to-primaryBtnHoverColor"></div>

                  {/* Action Buttons (Appears on hover like in JobPosts / Faqs) */}
                  <div className="absolute top-3.5 right-3.5 flex items-center gap-1.5 z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    <button
                      onClick={() => handleOpenViewPackage(pkg)}
                      className="p-2 bg-white/95 backdrop-blur-sm rounded-lg text-primaryBtnColor hover:bg-primaryBtnColor hover:text-white transition-all shadow-sm cursor-pointer"
                      title="View Details"
                    >
                      <RiEyeLine size={15} />
                    </button>
                    <button
                      onClick={() => handleOpenEditPackage(pkg)}
                      className="p-2 bg-white/95 backdrop-blur-sm rounded-lg text-blue-600 hover:bg-blue-600 hover:text-white transition-all shadow-sm cursor-pointer"
                      title="Edit Package"
                    >
                      <RiEditLine size={15} />
                    </button>
                    <button
                      onClick={() => handleDeletePackage(pkg._id, pkg.name)}
                      className="p-2 bg-white/95 backdrop-blur-sm rounded-lg text-red-600 hover:bg-red-600 hover:text-white transition-all shadow-sm cursor-pointer"
                      title="Delete Package"
                    >
                      <RiDeleteBinLine size={15} />
                    </button>
                  </div>

                  {/* Card Header & Content */}
                  <div className="p-6">
                    {/* Title and Badge */}
                    <div className="flex items-start justify-between gap-2 mb-3">
                      <div>
                        <h3 className="text-xl font-heading font-black text-primaryTextColor uppercase tracking-tight group-hover:text-primaryBtnHoverColor transition-colors">
                          {pkg.name}
                        </h3>
                        <p className="text-xs text-gray-500 font-subheading mt-0.5">
                          {pkg.partySize} (Max {pkg.maxParticipants} players)
                        </p>
                      </div>
                      {pkg.badge && (
                        <span className="bg-red-50 text-primaryBtnHoverColor border border-primaryBtnHoverColor/20 text-[10px] font-heading font-bold uppercase px-2.5 py-0.5 rounded-full flex items-center gap-1 shadow-2xs">
                          <Sparkles size={10} /> {pkg.badge}
                        </span>
                      )}
                    </div>

                    {/* Price Display */}
                    <div className="bg-gray-50/70 border border-gray-100 rounded-xl p-4 my-4 flex items-baseline justify-between">
                      <div>
                        <span className="text-3xl font-heading font-black text-primaryBtnColor">
                          ${pkg.price}
                        </span>
                        <span className="text-xs text-gray-500 font-subheading ml-1.5">
                          flat base rate
                        </span>
                      </div>
                      <span className="text-xs font-heading font-bold text-primaryBtnHoverColor">
                        +${pkg.extraParticipantPrice}/extra player
                      </span>
                    </div>

                    {/* Key Metric Tags */}
                    <div className="grid grid-cols-2 gap-2 text-xs font-subheading text-gray-600 mb-4">
                      <div className="p-2.5 rounded-lg bg-gray-50 border border-gray-100 flex items-center gap-2">
                        <RiTimeLine className="text-primaryBtnHoverColor" />
                        <span>{pkg.sportsActivityTime}</span>
                      </div>
                      <div className="p-2.5 rounded-lg bg-gray-50 border border-gray-100 flex items-center gap-2">
                        <Users size={14} className="text-primaryBtnColor" />
                        <span>{pkg.partySocialAreaTime} room</span>
                      </div>
                    </div>

                    {/* Sport Choice Note */}
                    <div className="mb-4">
                      <span className="text-[10px] font-heading font-extrabold text-gray-400 uppercase tracking-wider block mb-1.5">
                        SPORTS INCLUDED:
                      </span>
                      <div className="flex flex-wrap gap-1.5">
                        {pkg.availableSports.map((sp, idx) => (
                          <span
                            key={idx}
                            className="text-[11px] font-semibold bg-gray-100 text-gray-700 px-2.5 py-1 rounded-md"
                          >
                            {sp}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Inclusions List */}
                    <div>
                      <span className="text-[10px] font-heading font-extrabold text-gray-400 uppercase tracking-wider block mb-2">
                        PACKAGE INCLUSIONS:
                      </span>
                      <ul className="space-y-1.5 text-xs text-gray-600 font-subheading">
                        {pkg.features.slice(0, 4).map((feat, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <RiCheckLine className="text-emerald-500 mt-0.5 flex-shrink-0 text-xs" />
                            <span className="line-clamp-1">{feat}</span>
                          </li>
                        ))}
                        {pkg.features.length > 4 && (
                          <li className="text-[11px] text-gray-400 font-semibold italic pl-5">
                            +{pkg.features.length - 4} more inclusions...
                          </li>
                        )}
                      </ul>
                    </div>
                  </div>

                  {/* Card Footer: Active Switch & Quick Edit */}
                  <div className="px-6 py-4 bg-gray-50/80 border-t border-gray-100 flex items-center justify-between text-xs font-subheading">
                    <div className="flex items-center gap-2">
                      <ToggleSwitch
                        checked={pkg.isActive}
                        onChange={() => handleTogglePackageStatus(pkg._id)}
                      />
                      <span
                        className={`font-semibold text-xs ${pkg.isActive ? "text-emerald-600" : "text-gray-400"
                          }`}
                      >
                        {pkg.isActive ? "Active Online" : "Hidden / Inactive"}
                      </span>
                    </div>

                    <button
                      onClick={() => handleOpenEditPackage(pkg)}
                      className="text-primaryBtnColor hover:text-primaryBtnHoverColor font-heading font-bold text-xs flex items-center gap-1 cursor-pointer transition-colors"
                    >
                      <span>Edit Settings</span>
                      <ArrowUpRight size={13} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* ===================== TAB 2: ADD-ONS & UPGRADES ===================== */}
      {activeTab === "addons" && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {filteredAddons.map((addon) => (
            <div
              key={addon._id}
              className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 relative border border-gray-100 p-6 flex flex-col justify-between"
            >
              {/* Action buttons */}
              <div className="absolute top-4 right-4 flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                <button
                  onClick={() => handleOpenEditAddon(addon)}
                  className="p-2 bg-gray-100 hover:bg-blue-500 hover:text-white rounded-lg text-blue-600 transition-colors shadow-2xs cursor-pointer"
                  title="Edit Add-on"
                >
                  <RiEditLine size={14} />
                </button>
                <button
                  onClick={() => handleDeleteAddon(addon._id, addon.title)}
                  className="p-2 bg-gray-100 hover:bg-red-500 hover:text-white rounded-lg text-red-600 transition-colors shadow-2xs cursor-pointer"
                  title="Delete Add-on"
                >
                  <RiDeleteBinLine size={14} />
                </button>
              </div>

              <div>
                <span className="text-[10px] font-heading font-bold text-primaryBtnHoverColor uppercase px-2.5 py-0.5 bg-red-50 rounded-full border border-primaryBtnHoverColor/20 inline-block mb-3">
                  {addon.category}
                </span>

                <h3 className="font-heading font-bold text-base text-primaryTextColor uppercase tracking-wide leading-snug">
                  {addon.title}
                </h3>

                <p className="text-gray-600 font-subheading text-xs sm:text-sm mt-2.5 leading-relaxed">
                  {addon.desc}
                </p>
              </div>

              <div className="mt-5 pt-4 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500 font-subheading">
                <span className="flex items-center gap-1 text-emerald-600 font-medium text-xs">
                  <RiCheckLine /> Active in inquiry form
                </span>
                <button
                  onClick={() => handleOpenEditAddon(addon)}
                  className="text-primaryBtnColor hover:text-primaryBtnHoverColor font-heading font-bold text-xs cursor-pointer"
                >
                  Modify
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ===================== TAB 3: NOTICE & POLICIES ===================== */}
      {activeTab === "policies" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
          {filteredPolicies.map((pol) => (
            <div
              key={pol._id}
              className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 relative border border-gray-100 p-6 flex flex-col justify-between"
            >
              {/* Action buttons */}
              <div className="absolute top-4 right-4 flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                <button
                  onClick={() => handleOpenEditPolicy(pol)}
                  className="p-2 bg-gray-100 hover:bg-blue-500 hover:text-white rounded-lg text-blue-600 transition-colors shadow-2xs cursor-pointer"
                  title="Edit Policy"
                >
                  <RiEditLine size={14} />
                </button>
                <button
                  onClick={() => handleDeletePolicy(pol._id, pol.title)}
                  className="p-2 bg-gray-100 hover:bg-red-500 hover:text-white rounded-lg text-red-600 transition-colors shadow-2xs cursor-pointer"
                  title="Delete Policy"
                >
                  <RiDeleteBinLine size={14} />
                </button>
              </div>

              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-[10px] font-heading font-bold text-gray-500 uppercase px-2 py-0.5 bg-gray-100 rounded-md">
                    {pol.category}
                  </span>
                </div>

                <h3 className="font-heading font-bold text-base text-primaryTextColor leading-snug">
                  {pol.title}
                </h3>

                <p className="text-gray-600 font-subheading text-xs sm:text-sm mt-2 leading-relaxed">
                  {pol.desc}
                </p>
              </div>

              <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between text-xs text-gray-400 font-subheading">
                <span>Strikes &amp; Shots Legal Notice</span>
                <button
                  onClick={() => handleOpenEditPolicy(pol)}
                  className="text-primaryBtnColor hover:text-primaryBtnHoverColor font-heading font-bold text-xs cursor-pointer"
                >
                  Edit Rule
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ===================== MODAL: CREATE / EDIT PACKAGE ===================== */}
      {isPackageModalOpen && (
        <ModalWrapper
          title={isEditing ? `Edit Package: ${packageFormData.name}` : "Create New Squad Package"}
          onClose={() => setIsPackageModalOpen(false)}
          maxWidth="max-w-3xl"
        >
          <form onSubmit={handleSavePackage} className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Package Name */}
              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                  Package Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Small Squad Party"
                  value={packageFormData.name}
                  onChange={(e) =>
                    setPackageFormData({ ...packageFormData, name: e.target.value })
                  }
                  className="w-full px-3.5 py-2.5 bg-white border border-gray-300 rounded-xl text-sm text-gray-900 focus:outline-none focus:border-primaryBtnHoverColor"
                />
              </div>

              {/* Price */}
              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                  Price ($ USD) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  required
                  min="0"
                  placeholder="e.g. 275"
                  value={packageFormData.price}
                  onChange={(e) =>
                    setPackageFormData({ ...packageFormData, price: e.target.value })
                  }
                  className="w-full px-3.5 py-2.5 bg-white border border-gray-300 rounded-xl text-sm text-gray-900 focus:outline-none focus:border-primaryBtnHoverColor"
                />
              </div>

              {/* Party Size Display Pill */}
              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                  Party Size Caption
                </label>
                <input
                  type="text"
                  placeholder="e.g. Up to 10 players"
                  value={packageFormData.partySize}
                  onChange={(e) =>
                    setPackageFormData({ ...packageFormData, partySize: e.target.value })
                  }
                  className="w-full px-3.5 py-2.5 bg-white border border-gray-300 rounded-xl text-sm text-gray-900 focus:outline-none focus:border-primaryBtnHoverColor"
                />
              </div>

              {/* Extra player price */}
              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                  Extra Player Fee ($ / person)
                </label>
                <input
                  type="number"
                  min="0"
                  placeholder="e.g. 20"
                  value={packageFormData.extraParticipantPrice}
                  onChange={(e) =>
                    setPackageFormData({
                      ...packageFormData,
                      extraParticipantPrice: e.target.value,
                    })
                  }
                  className="w-full px-3.5 py-2.5 bg-white border border-gray-300 rounded-xl text-sm text-gray-900 focus:outline-none focus:border-primaryBtnHoverColor"
                />
              </div>

              {/* Base & Max participants */}
              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                  Base Included Players
                </label>
                <input
                  type="number"
                  min="1"
                  value={packageFormData.baseParticipants}
                  onChange={(e) =>
                    setPackageFormData({
                      ...packageFormData,
                      baseParticipants: Number(e.target.value),
                    })
                  }
                  className="w-full px-3.5 py-2.5 bg-white border border-gray-300 rounded-xl text-sm text-gray-900 focus:outline-none focus:border-primaryBtnHoverColor"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                  Max Allowed Players (Before Upgrade)
                </label>
                <input
                  type="number"
                  min="1"
                  value={packageFormData.maxParticipants}
                  onChange={(e) =>
                    setPackageFormData({
                      ...packageFormData,
                      maxParticipants: Number(e.target.value),
                    })
                  }
                  className="w-full px-3.5 py-2.5 bg-white border border-gray-300 rounded-xl text-sm text-gray-900 focus:outline-none focus:border-primaryBtnHoverColor"
                />
              </div>

              {/* Durations */}
              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                  Sports Activity Duration
                </label>
                <input
                  type="text"
                  placeholder="e.g. 90 minutes"
                  value={packageFormData.sportsActivityTime}
                  onChange={(e) =>
                    setPackageFormData({
                      ...packageFormData,
                      sportsActivityTime: e.target.value,
                    })
                  }
                  className="w-full px-3.5 py-2.5 bg-white border border-gray-300 rounded-xl text-sm text-gray-900 focus:outline-none focus:border-primaryBtnHoverColor"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                  Private Party Bay Time
                </label>
                <input
                  type="text"
                  placeholder="e.g. 45–60 minutes"
                  value={packageFormData.partySocialAreaTime}
                  onChange={(e) =>
                    setPackageFormData({
                      ...packageFormData,
                      partySocialAreaTime: e.target.value,
                    })
                  }
                  className="w-full px-3.5 py-2.5 bg-white border border-gray-300 rounded-xl text-sm text-gray-900 focus:outline-none focus:border-primaryBtnHoverColor"
                />
              </div>
            </div>

            {/* Sport Selection Scope */}
            <div>
              <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                Sport Choice Rules
              </label>
              <select
                value={packageFormData.sportChoice}
                onChange={(e) =>
                  setPackageFormData({
                    ...packageFormData,
                    sportChoice: e.target.value,
                    availableSports:
                      e.target.value === "Soccer Only"
                        ? ["Indoor Soccer"]
                        : [
                          "Indoor Soccer",
                          "Basketball",
                          "Volleyball",
                          "Indoor Cricket",
                        ],
                  })
                }
                className="w-full px-3.5 py-2.5 bg-white border border-gray-300 rounded-xl text-sm text-gray-900 focus:outline-none focus:border-primaryBtnHoverColor cursor-pointer"
              >
                <option value="Soccer Only">⚽ Soccer Only (Locked for Small Squad)</option>
                <option value="Choose 1 Sport">🏆 Choose 1 of 4 Major Sports (Soccer, Basketball, Volleyball, Cricket)</option>
                <option value="All Sports & Dual">⭐ Custom / Arena Buyout</option>
              </select>
            </div>

            {/* Badge & Popular Switch */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-center bg-gray-50 p-4 rounded-xl border border-gray-200">
              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                  Card Badge Text (Optional)
                </label>
                <input
                  type="text"
                  placeholder="e.g. YOUR BEST MATCH, ULTIMATE VALUE"
                  value={packageFormData.badge}
                  onChange={(e) =>
                    setPackageFormData({ ...packageFormData, badge: e.target.value })
                  }
                  className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-xs text-gray-900 focus:outline-none focus:border-primaryBtnHoverColor"
                />
              </div>

              <div className="flex items-center justify-between sm:justify-end gap-6 pt-2">
                <label className="flex items-center gap-2 cursor-pointer text-xs font-bold text-gray-700 uppercase">
                  <input
                    type="checkbox"
                    checked={packageFormData.isPopular}
                    onChange={(e) =>
                      setPackageFormData({
                        ...packageFormData,
                        isPopular: e.target.checked,
                      })
                    }
                    className="w-4 h-4 text-primaryBtnHoverColor rounded"
                  />
                  <span>Mark as Popular</span>
                </label>

                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold text-gray-700 uppercase">
                    Active:
                  </span>
                  <ToggleSwitch
                    checked={packageFormData.isActive}
                    onChange={() =>
                      setPackageFormData({
                        ...packageFormData,
                        isActive: !packageFormData.isActive,
                      })
                    }
                  />
                </div>
              </div>
            </div>

            {/* Inclusions List */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-bold text-gray-700 uppercase">
                  Package Inclusions &amp; Features
                </label>
                <button
                  type="button"
                  onClick={handleAddFeature}
                  className="text-xs font-bold text-primaryBtnHoverColor hover:text-red-800 flex items-center gap-1 cursor-pointer"
                >
                  <RiAddLine /> Add Feature
                </button>
              </div>

              <div className="space-y-2">
                {packageFormData.features.map((feat, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <input
                      type="text"
                      placeholder={`Feature #${idx + 1}`}
                      value={feat}
                      onChange={(e) => handleFeatureChange(idx, e.target.value)}
                      className="flex-1 px-3 py-2 bg-white border border-gray-300 rounded-lg text-xs sm:text-sm text-gray-800 focus:outline-none focus:border-primaryBtnHoverColor"
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveFeature(idx)}
                      disabled={packageFormData.features.length <= 1}
                      className="p-2 text-gray-400 hover:text-red-500 disabled:opacity-30 cursor-pointer"
                    >
                      <RiCloseLine size={18} />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Modal Actions */}
            <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={() => setIsPackageModalOpen(false)}
                className="px-5 py-2.5 rounded-xl border border-gray-300 text-gray-700 hover:bg-gray-100 text-xs font-heading font-bold uppercase tracking-wider cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-primaryBtnColor to-primaryBtnHoverColor text-white text-xs font-heading font-bold uppercase tracking-wider shadow-lg hover:shadow-xl transition-all cursor-pointer disabled:opacity-50 flex items-center gap-2"
              >
                {submitting ? (
                  <span>Saving...</span>
                ) : (
                  <span>{isEditing ? "Save Changes" : "Create Package"}</span>
                )}
              </button>
            </div>
          </form>
        </ModalWrapper>
      )}

      {/* ===================== MODAL: VIEW DETAILS ===================== */}
      {isViewModalOpen && selectedItem && (
        <ModalWrapper
          title="Package Specifications Preview"
          onClose={() => setIsViewModalOpen(false)}
          maxWidth="max-w-2xl"
        >
          <div className="space-y-4">
            <div className="flex items-center justify-between pb-3 border-b border-gray-200">
              <div>
                <h3 className="text-2xl font-black font-heading text-primaryTextColor">
                  {selectedItem.name}
                </h3>
                <p className="text-xs text-gray-500 font-subheading mt-0.5">
                  Slug identifier: <code className="bg-gray-100 px-1 py-0.5 rounded">{selectedItem.slug}</code>
                </p>
              </div>
              <div className="text-right">
                <div className="text-3xl font-heading font-black text-primaryBtnColor">
                  ${selectedItem.price}
                </div>
                <span className="text-[11px] text-gray-500 font-subheading">
                  +${selectedItem.extraParticipantPrice}/extra guest
                </span>
              </div>
            </div>

            {/* Grid Specs */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs font-subheading">
              <div className="p-3 bg-gray-50 rounded-xl">
                <span className="text-gray-400 block text-[10px] uppercase font-bold">Party Size</span>
                <span className="font-bold text-gray-800 text-sm mt-0.5 block">{selectedItem.partySize}</span>
              </div>
              <div className="p-3 bg-gray-50 rounded-xl">
                <span className="text-gray-400 block text-[10px] uppercase font-bold">Max Players</span>
                <span className="font-bold text-gray-800 text-sm mt-0.5 block">{selectedItem.maxParticipants} players</span>
              </div>
              <div className="p-3 bg-gray-50 rounded-xl">
                <span className="text-gray-400 block text-[10px] uppercase font-bold">Activity Duration</span>
                <span className="font-bold text-gray-800 text-sm mt-0.5 block">{selectedItem.sportsActivityTime}</span>
              </div>
              <div className="p-3 bg-gray-50 rounded-xl">
                <span className="text-gray-400 block text-[10px] uppercase font-bold">Party Bay Time</span>
                <span className="font-bold text-gray-800 text-sm mt-0.5 block">{selectedItem.partySocialAreaTime}</span>
              </div>
              <div className="p-3 bg-gray-50 rounded-xl">
                <span className="text-gray-400 block text-[10px] uppercase font-bold">Sport Selection</span>
                <span className="font-bold text-gray-800 text-sm mt-0.5 block">{selectedItem.sportChoice}</span>
              </div>
              <div className="p-3 bg-gray-50 rounded-xl">
                <span className="text-gray-400 block text-[10px] uppercase font-bold">Current Status</span>
                <span className={`font-bold text-sm mt-0.5 block ${selectedItem.isActive ? "text-emerald-600" : "text-gray-400"}`}>
                  {selectedItem.isActive ? "Active Online" : "Inactive"}
                </span>
              </div>
            </div>

            {/* Features */}
            <div>
              <h4 className="text-xs font-bold text-gray-700 uppercase mb-2">
                Included Features Checklist:
              </h4>
              <ul className="space-y-1.5 bg-gray-50/50 p-4 rounded-xl border border-gray-200 text-xs text-gray-700 font-subheading">
                {selectedItem.features?.map((f, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <RiCheckLine className="text-emerald-500" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="flex justify-end pt-3">
              <button
                onClick={() => setIsViewModalOpen(false)}
                className="px-5 py-2 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-800 text-xs font-heading font-bold uppercase cursor-pointer"
              >
                Close Preview
              </button>
            </div>
          </div>
        </ModalWrapper>
      )}

      {/* ===================== MODAL: CREATE / EDIT ADD-ON ===================== */}
      {isAddonModalOpen && (
        <ModalWrapper
          title={isEditing ? "Edit Custom Add-on" : "Create New Custom Add-on"}
          onClose={() => setIsAddonModalOpen(false)}
          maxWidth="max-w-lg"
        >
          <form onSubmit={handleSaveAddon} className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                Add-on Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                placeholder="e.g. ADDITIONAL SPORT"
                value={addonFormData.title}
                onChange={(e) =>
                  setAddonFormData({ ...addonFormData, title: e.target.value })
                }
                className="w-full px-3.5 py-2.5 bg-white border border-gray-300 rounded-xl text-sm text-gray-900 focus:outline-none focus:border-primaryBtnHoverColor"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                Category
              </label>
              <select
                value={addonFormData.category}
                onChange={(e) =>
                  setAddonFormData({ ...addonFormData, category: e.target.value })
                }
                className="w-full px-3.5 py-2.5 bg-white border border-gray-300 rounded-xl text-sm text-gray-900 focus:outline-none focus:border-primaryBtnHoverColor cursor-pointer"
              >
                <option value="Player Capacity">Player Capacity</option>
                <option value="Sports Customization">Sports Customization</option>
                <option value="Catering">Catering &amp; Refreshment</option>
                <option value="Large Groups">Large Groups</option>
                <option value="Experience">Experience &amp; Extras</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                Description
              </label>
              <textarea
                rows="3"
                placeholder="Details of the add-on offering..."
                value={addonFormData.desc}
                onChange={(e) =>
                  setAddonFormData({ ...addonFormData, desc: e.target.value })
                }
                className="w-full px-3.5 py-2.5 bg-white border border-gray-300 rounded-xl text-sm text-gray-900 focus:outline-none focus:border-primaryBtnHoverColor"
              ></textarea>
            </div>

            <div className="flex items-center justify-end gap-3 pt-3 border-t border-gray-200">
              <button
                type="button"
                onClick={() => setIsAddonModalOpen(false)}
                className="px-4 py-2 border border-gray-300 rounded-xl text-xs font-bold text-gray-600 hover:bg-gray-100 cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-5 py-2 bg-gradient-to-r from-primaryBtnColor to-primaryBtnHoverColor text-white rounded-xl text-xs font-heading font-bold uppercase shadow-md cursor-pointer"
              >
                {isEditing ? "Save Add-on" : "Create Add-on"}
              </button>
            </div>
          </form>
        </ModalWrapper>
      )}

      {/* ===================== MODAL: CREATE / EDIT POLICY ===================== */}
      {isPolicyModalOpen && (
        <ModalWrapper
          title={isEditing ? "Edit Facility Policy" : "Create New Facility Policy"}
          onClose={() => setIsPolicyModalOpen(false)}
          maxWidth="max-w-lg"
        >
          <form onSubmit={handleSavePolicy} className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                Policy Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                placeholder="e.g. Participant Count"
                value={policyFormData.title}
                onChange={(e) =>
                  setPolicyFormData({ ...policyFormData, title: e.target.value })
                }
                className="w-full px-3.5 py-2.5 bg-white border border-gray-300 rounded-xl text-sm text-gray-900 focus:outline-none focus:border-primaryBtnHoverColor"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                Category
              </label>
              <select
                value={policyFormData.category}
                onChange={(e) =>
                  setPolicyFormData({ ...policyFormData, category: e.target.value })
                }
                className="w-full px-3.5 py-2.5 bg-white border border-gray-300 rounded-xl text-sm text-gray-900 focus:outline-none focus:border-primaryBtnHoverColor cursor-pointer"
              >
                <option value="General">General Notice</option>
                <option value="Sports">Sports &amp; Playing Area</option>
                <option value="Facility">Facility &amp; Courts</option>
                <option value="Catering">Food &amp; Refreshments</option>
                <option value="Supplies">Party Supplies</option>
                <option value="Booking">Booking &amp; Rescheduling</option>
                <option value="Legal & Safety">Legal, Waivers &amp; Safety</option>
                <option value="Group Events">Large Group Policy</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                Full Policy Description <span className="text-red-500">*</span>
              </label>
              <textarea
                rows="4"
                required
                placeholder="Full rule explanation displayed on the Event Packages and Waiver submission flow..."
                value={policyFormData.desc}
                onChange={(e) =>
                  setPolicyFormData({ ...policyFormData, desc: e.target.value })
                }
                className="w-full px-3.5 py-2.5 bg-white border border-gray-300 rounded-xl text-sm text-gray-900 focus:outline-none focus:border-primaryBtnHoverColor"
              ></textarea>
            </div>

            <div className="flex items-center justify-end gap-3 pt-3 border-t border-gray-200">
              <button
                type="button"
                onClick={() => setIsPolicyModalOpen(false)}
                className="px-4 py-2 border border-gray-300 rounded-xl text-xs font-bold text-gray-600 hover:bg-gray-100 cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-5 py-2 bg-gradient-to-r from-primaryBtnColor to-primaryBtnHoverColor text-white rounded-xl text-xs font-heading font-bold uppercase shadow-md cursor-pointer"
              >
                {isEditing ? "Save Policy" : "Create Policy"}
              </button>
            </div>
          </form>
        </ModalWrapper>
      )}

      {/* ===================== TAB 4: FOOD & BEVERAGE ===================== */}
      {activeTab === "food" && (
        <>
          {/* Section Visibility Toggle */}
          <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm flex items-center justify-between mb-6">
            <div>
              <h3 className="text-sm font-bold text-gray-800">Show Food &amp; Beverage Section on Website</h3>
              <p className="text-xs text-gray-500 mt-1">If turned off, the entire Food &amp; Beverage section will be hidden from users.</p>
            </div>
            <div className="flex items-center gap-3">
              <span className={`text-xs font-bold ${isFoodSectionVisible ? "text-emerald-600" : "text-gray-400"}`}>
                {isFoodSectionVisible ? "Visible" : "Hidden"}
              </span>
              <ToggleSwitch
                checked={isFoodSectionVisible}
                onChange={handleToggleFoodSectionVisibility}
              />
            </div>
          </div>

          {loading ? (
            <div className="text-center py-10">Loading menu items...</div>
          ) : filteredFoodCards.length === 0 ? (
            <div className="bg-white rounded-2xl p-12 text-center border border-gray-100 shadow-sm mb-12">
              <Pizza size={48} className="mx-auto text-gray-300 mb-3" />
              <h3 className="text-lg font-heading font-bold text-gray-700">No Items Found</h3>
              <p className="text-xs text-gray-500 font-subheading mt-1 max-w-sm mx-auto">
                No food items match your search or you haven't added any yet.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-12">
              {filteredFoodCards.map((card) => (
                <div
                  key={card._id}
                  className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all border border-gray-100 flex flex-col justify-between"
                >
                  <div className="p-5 flex-1 flex flex-col">
                    <div className="flex justify-between items-start mb-4">
                      <div className="w-12 h-12 rounded-xl bg-blue-50 text-primaryBtnColor flex items-center justify-center text-2xl">
                        {iconMapping[card.iconName] || <FaUtensils />}
                      </div>
                      <div className="flex gap-2 opacity-100 lg:opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => handleOpenEditFood(card)}
                          className="p-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-primaryBtnColor hover:text-white transition-colors"
                        >
                          <RiEditLine size={16} />
                        </button>
                        <button
                          onClick={() => handleDeleteFood(card._id, card.title)}
                          className="p-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-red-600 hover:text-white transition-colors"
                        >
                          <RiDeleteBinLine size={16} />
                        </button>
                      </div>
                    </div>
                    <h3 className="text-lg font-heading font-bold text-gray-800">
                      {card.title}
                    </h3>
                    <p className="text-xs font-semibold text-primaryBtnHoverColor mt-1">
                      {card.tagline}
                    </p>
                    <p className="text-sm text-gray-600 mt-3 line-clamp-3">
                      {card.desc}
                    </p>
                    <div className="mt-4 pt-4 border-t border-gray-100">
                      <p className="text-xs font-bold text-gray-500 uppercase">
                        {card.options?.length} Options Included
                      </p>
                    </div>
                  </div>

                  {/* Card Footer: Active Switch */}
                  <div className="px-5 py-4 bg-gray-50/80 border-t border-gray-100 flex items-center justify-between text-xs font-subheading">
                    <div className="flex items-center gap-2">
                      <ToggleSwitch
                        checked={card.isActive}
                        onChange={() => handleToggleFoodStatus(card._id)}
                      />
                      <span
                        className={`font-semibold text-xs ${card.isActive ? "text-emerald-600" : "text-gray-400"
                          }`}
                      >
                        {card.isActive ? "Active Online" : "Hidden / Inactive"}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* ===================== MODAL: CREATE / EDIT FOOD ===================== */}
      {isFoodModalOpen && (
        <ModalWrapper
          isOpen={isFoodModalOpen}
          onClose={() => setIsFoodModalOpen(false)}
          title={isEditing ? "Edit Menu Item" : "Create Menu Item"}
          maxWidth="max-w-2xl"
        >
          <form onSubmit={handleSaveFood} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Title *
                </label>
                <input
                  type="text"
                  required
                  value={foodFormData.title}
                  onChange={(e) => setFoodFormData({ ...foodFormData, title: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primaryBtnHoverColor focus:ring-1 focus:ring-primaryBtnHoverColor"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Tagline
                </label>
                <input
                  type="text"
                  value={foodFormData.tagline}
                  onChange={(e) => setFoodFormData({ ...foodFormData, tagline: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primaryBtnHoverColor focus:ring-1 focus:ring-primaryBtnHoverColor"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Description *
              </label>
              <textarea
                required
                rows={3}
                value={foodFormData.desc}
                onChange={(e) => setFoodFormData({ ...foodFormData, desc: e.target.value })}
                className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primaryBtnHoverColor focus:ring-1 focus:ring-primaryBtnHoverColor resize-none"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Icon
              </label>
              <select
                value={foodFormData.iconName}
                onChange={(e) => setFoodFormData({ ...foodFormData, iconName: e.target.value })}
                className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primaryBtnHoverColor focus:ring-1 focus:ring-primaryBtnHoverColor cursor-pointer"
              >
                {iconOptions.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-xs font-semibold text-gray-700">
                  Menu Options
                </label>
                <button
                  type="button"
                  onClick={handleAddOption}
                  className="text-xs font-semibold text-primaryBtnColor hover:text-primaryBtnHoverColor cursor-pointer"
                >
                  + Add Option
                </button>
              </div>
              <div className="space-y-3">
                {foodFormData.options.map((opt, idx) => (
                  <div key={idx} className="flex gap-2 items-start">
                    <div className="flex-1 space-y-2">
                      <input
                        type="text"
                        placeholder="Option Name"
                        required
                        value={opt.name}
                        onChange={(e) => handleOptionChange(idx, "name", e.target.value)}
                        className="w-full px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-sm"
                      />
                      <input
                        type="text"
                        placeholder="Option Description"
                        value={opt.desc}
                        onChange={(e) => handleOptionChange(idx, "desc", e.target.value)}
                        className="w-full px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-sm"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveOption(idx)}
                      disabled={foodFormData.options.length <= 1}
                      className="mt-1 p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg disabled:opacity-50 cursor-pointer"
                    >
                      <RiDeleteBinLine size={16} />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 pt-3 border-t border-gray-200 mt-4">
              <button
                type="button"
                onClick={() => setIsFoodModalOpen(false)}
                className="px-4 py-2 border border-gray-300 rounded-xl text-xs font-bold text-gray-600 hover:bg-gray-100 cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="px-5 py-2 bg-gradient-to-r from-primaryBtnColor to-primaryBtnHoverColor text-white rounded-xl text-xs font-heading font-bold uppercase shadow-md cursor-pointer disabled:opacity-50 flex items-center gap-2"
              >
                {submitting ? "Saving..." : <>{isEditing ? "Save Item" : "Create Item"}</>}
              </button>
            </div>
          </form>
        </ModalWrapper>
      )}
    </div>
  );
};

export default AdminEventPackages;
